import React from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { LogOut, ShieldCheck, Home, User } from 'lucide-react';
import logo from '../assets/logo.jpeg';
import Footer from '../components/common/Footer.jsx';

const navItems = [
  { path: '/dashboard', label: 'Overview', icon: Home, end: true },
  { path: '/dashboard/profile', label: 'Profile', icon: User, end: false }
];

const DashboardLayout = () => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden flex flex-col justify-between">
      
      {/* Background glowing orbs & radial overlay matching homepage UI */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-0 right-0 h-[600px] bg-[radial-gradient(ellipse_at_top,_rgba(34,211,238,0.06),transparent_60%)]" />
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-full blur-[120px] opacity-20" />
        <div className="absolute bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-purple-500/5 to-pink-600/5 rounded-full blur-[100px] opacity-15" />
      </div>

      {/* Background Watermark Logo */}
      <div className="pointer-events-none select-none fixed inset-0 z-30 flex items-center justify-center overflow-hidden opacity-[0.018]">
        <img 
          src={logo} 
          alt="Watermark Logo" 
          className="w-[60vw] max-w-[600px] aspect-square object-contain filter invert mix-blend-screen" 
        />
      </div>

      <div className="flex-1 flex flex-col z-10 w-full">
        {/* Sticky Full-width Navigation Navbar */}
        <header className="sticky top-0 z-50 w-full border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-xl shadow-xl shadow-black/20">
          <div className="mx-auto flex max-w-7xl h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            
            {/* Logo & Brand Identity */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 rounded-xl bg-cyan-400/20 blur-md group-hover:blur-lg transition-all" />
                <div className="relative h-12 px-3 py-1.5 rounded-xl bg-white border border-slate-700/50 shadow-md flex items-center justify-center">
                  <img
                    src={logo}
                    alt="QBKart IT Solutions"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
              <div className="leading-none flex flex-col justify-center">
                <span className="text-xl font-bold tracking-tight text-white leading-none">
                  QB<span className="text-cyan-400">Kart</span>
                </span>
                <span className="mt-1 text-[8px] tracking-[0.25em] uppercase text-slate-400 leading-none">
                  TECHNOLOGIES
                </span>
              </div>
            </Link>

            {/* Sub-routing Navigation Links */}
            <nav className="hidden md:flex bg-slate-900/60 p-1 rounded-2xl border border-slate-800/40">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.label}
                    to={item.path}
                    end={item.end}
                    className={({ isActive }) => 
                      `flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs sm:text-sm font-bold tracking-wide transition-all ${
                        isActive 
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20' 
                          : 'text-slate-400 hover:text-white hover:bg-slate-900/30'
                      }`
                    }
                  >
                    <Icon size={15} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Client Profile & Sign Out */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex flex-col items-end leading-none">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Client Account</span>
                <span className="text-sm font-bold text-white mt-1 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-slate-700 bg-slate-950 flex-shrink-0">
                    <img 
                      src={user?.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=256&h=256'} 
                      alt="Profile Avatar" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {user?.displayName || (user?.email ? user.email.split('@')[0] : 'Client')}
                </span>
              </div>
              
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-5 py-2.5 text-sm font-bold text-red-400 hover:text-red-300 hover:border-red-500/40 hover:bg-red-500/10 transition-all duration-300 active:scale-95"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>

          </div>

          {/* Mobile subrouting navigation */}
          <div className="md:hidden w-full border-t border-slate-800/60 bg-slate-950 p-2 flex justify-around">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) => 
                    `flex flex-col items-center gap-1 rounded-xl px-3 py-1.5 text-[10px] font-bold transition-all ${
                      isActive 
                        ? 'text-cyan-400 font-extrabold' 
                        : 'text-slate-400 hover:text-white'
                    }`
                  }
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </header>

        {/* Content Wrapper */}
        <main className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Outlet />
        </main>
      </div>

      {/* Shared Footer component at bottom */}
      <Footer />
    </div>
  );
};

export default DashboardLayout;
